<!-- ZayaAI UI-9: project section selector, lifted into the main header next to the
     logo (replaces the standalone ProjectSectionBar below the navbar). Shows the
     current section and lists all configUI-enabled sections (active highlighted +
     checkmarked). Used by ImsNavbar on project pages. -->
<template>
<div class="secsel" v-click-outside="() => (open = false)">
  <button
    class="secsel-btn"
    :class="{open}"
    :aria-expanded="open ? 'true' : 'false'"
    aria-haspopup="true"
    @click="open = !open"
    @keydown.esc="open = false"
  >
    <span class="secsel-ico"><i :class="activeSection.icon"></i></span>
    <span class="secsel-lbl">{{ $t(activeSection.labelKey) }}</span>
    <span class="secsel-caret"><i class="fas" :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"></i></span>
  </button>

  <div v-if="open" class="secsel-menu" role="menu">
    <router-link
      v-for="section in sections"
      :key="section.key"
      :to="sectionRoute(section)"
      class="secsel-item"
      :class="{active: section.key === activeSection.key}"
      role="menuitem"
    >
      <span class="si"><i :class="section.icon"></i></span>
      <span class="secsel-item-lbl">{{ $t(section.labelKey) }}</span>
      <span v-if="section.key === activeSection.key" class="secsel-check"><i class="fas fa-check"></i></span>
    </router-link>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';

// key = configUI tab key (project-<key>-tab); route = path segment under /project/:id.
const SECTIONS = [
  {key: 'images', route: 'images', icon: 'far fa-image', labelKey: 'images'},
  {key: 'image-groups', route: 'image-groups', icon: 'far fa-images', labelKey: 'image-groups'},
  {key: 'annotations', route: 'annotations', icon: 'far fa-edit', labelKey: 'annotations'},
  // Two distinct pages, previously BOTH labelled "Pipelines": this one lists App Engine
  // task runs, while 'pipelines' below is where pipelines are launched and their queue,
  // progress and stop controls live. Renamed so the label says which is which.
  {key: 'apps', route: 'apps', icon: 'fas fa-code', labelKey: 'project-task-runs'},
  // Not gated by a configUI tab key (there is no project-pipelines-tab); it is admin-only,
  // matching ProjectSidebar. Without adminOnly it would be filtered out and the page would
  // be reachable only from the sidebar.
  {key: 'pipelines', route: 'pipelines', icon: 'fas fa-cogs', labelKey: 'pipelines', adminOnly: true},
  {key: 'activities', route: 'activity', icon: 'fas fa-tachometer-alt', labelKey: 'activity'},
  {key: 'information', route: 'information', icon: 'fas fa-info-circle', labelKey: 'information'},
  {key: 'configuration', route: 'configuration', icon: 'fas fa-cogs', labelKey: 'configuration'}
];

export default {
  name: 'project-section-selector',
  data() {
    return {open: false};
  },
  computed: {
    project: get('currentProject/project'),
    configUI: get('currentProject/configUI'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    sections() {
      return SECTIONS.filter(s => (
        s.adminOnly ? this.canManageProject : this.configUI[`project-${s.key}-tab`]
      ));
    },
    activeSection() {
      const seg = this.$route.path.split('/')[3] || '';
      return this.sections.find(s => s.route === seg) || this.sections[0] || SECTIONS[0];
    }
  },
  watch: {
    $route() {
      this.open = false;
    }
  },
  methods: {
    sectionRoute(section) {
      return `/project/${this.project.id}/${section.route}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.secsel {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.secsel-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px 0 5px;
  border: 1px solid #d4d9e2;
  border-radius: 8px;
  background: $surface;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: $ink-1;

  &:hover { background: #f8f9fb; }
  &.open { background: $blue-soft; border-color: $primary; }
}

.secsel-ico {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  border-radius: 6px;
  background: $primary;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  .fas, .far { padding-right: 0; }
}

.secsel-lbl {
  text-align: left;
  white-space: nowrap;
}

.secsel-caret {
  color: #8a91a1;
  display: inline-flex;
  .fas { font-size: 0.7rem; padding-right: 0; }
}

.secsel-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 236px;
  background: $surface;
  border: 1px solid $line-1;
  border-radius: 10px;
  box-shadow: 0 14px 38px rgba(15, 17, 22, 0.16);
  z-index: 60;
  padding: 6px;
}

.secsel-item {
  display: flex;
  align-items: center;
  gap: 11px;
  height: 40px;
  padding: 0 10px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: $ink-3;
  text-decoration: none;

  .si { width: 20px; display: inline-flex; justify-content: center; color: #8a91a1; }
  .si .fas, .si .far { padding-right: 0; }
  .secsel-item-lbl { flex: 1; }
  &:hover { background: #f2f4f8; color: $ink-1; }
  &:hover .si { color: $ink-3; }
  &.active { background: $blue-soft; color: $dark-primary; font-weight: 600; }
  &.active .si { color: $primary; }
}

.secsel-check {
  margin-left: auto;
  color: $primary;
  display: inline-flex;
  .fas { padding-right: 0; }
}
</style>
